import React from 'react';
import { Button, Typography } from '@notch-ordering/ui-components';
import { PageTitle } from '@ar/components/PageTitle';
import CustomerPortalBackground from '@ap/assets/images/customer-portal-background.png';
import { useMutation } from '@tanstack/react-query';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { resendPortalInvite } from '@/ar/network/AccountsReceivable.network';

/**
 * The AP Magic Link Request page
 *
 * @returns JSX Element
 */
export const MagicLinkRequestPage = function (): JSX.Element {
    useDocumentTitle('Magic Link Request');
    const [isLinkSent, setIsLinkSent] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);
    const searchParams = new URLSearchParams(window.location.search);
    const email = searchParams.get('email');
    const returnPath = searchParams.get('return_path');
    const customerId = searchParams.get('customer_id');

    const resendPortalInviteMutation = useMutation(resendPortalInvite);

    const handleSendNewLink = () => {
        resendPortalInviteMutation.mutate({
            body: {
                email,
                return_path: returnPath,
                customer_id: customerId
            }
        }, {
            onSuccess: () => {
                setIsLinkSent(true);
            },
            onError: () => {
                setHasError(true);
            }
        });
    };

    const linkExpiredBody = (
        <>
            <PageTitle>You need a new link</PageTitle>
            <div>
                <Typography variant="LG-1" className="text-gray-700 leading-5">Hey there! 👋</Typography>
                <Typography variant="LG-1" className="text-gray-700 leading-5">Looks like the Customer Portal link you are trying to access is no longer valid.</Typography>
                <Typography variant="LG-1" className="text-gray-700 leading-5 mb-0">Not to worry, simply click “<strong>Send me a new link</strong>” and a new link will be sent to the email on file.</Typography>
            </div>
            <Button variant="SECONDARY" className="mb-4" onClick={handleSendNewLink}>Send me a new link!</Button>
        </>
    );

    const linkSentBody = (
        <>
            <PageTitle>Check your inbox</PageTitle>
            <div>
                <Typography variant="LG-1" className="text-gray-700 leading-5">A new link has been sent to your email on file.</Typography>
                <Typography variant="LG-1" className="text-gray-700 leading-5">If this issue persists, please contact your supplier.</Typography>
            </div>
        </>
    );

    const linkSentErrorBody = (
        <>
            <div>
                <Typography variant="LG-1" className="text-gray-700 leading-5">We attempted to send an access link to the email associated with this access link, but an error occurred.</Typography>
                <Typography variant="LG-1" className="text-gray-700 leading-5">If this issue continues, please reach out to your supplier for assistance.</Typography>
            </div>
        </>
    );

    const getBodyContent = () => {
        if (isLinkSent) {
            return linkSentBody;
        }
        if (hasError) {
            return linkSentErrorBody;
        }
        return linkExpiredBody;
    };

    return (
        <div style={{
            backgroundImage: `url(${CustomerPortalBackground})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        }}>
            <div className="bg-white rounded-lg flex flex-col items-start w-[586px] h-[380px] p-8 gap-8">
                {getBodyContent()}
                <Typography variant="BASE" className="text-gray-700 mb-0 mt-auto">Powered by <strong>Notch Financial</strong></Typography>
            </div>
        </div>
    );
};
